/**
 * PortlessBlockedList (BLU-713, rebuilt for parity in BLU-732) — orders this
 * floor cannot label.
 *
 * A shipping *failure* is something that already went wrong at the ship step;
 * this is the earlier state — Portless has not issued a label, so the order
 * cannot even be batched yet. Same tab because it is the same operator asking
 * the same question: why can't this parcel leave?
 *
 * The list is derived from the open episode rows the Slack digest sends from,
 * so the floor view and the Slack message can never disagree. Card anatomy,
 * search, and pagination mirror the other issues tabs (ShippingFailuresList /
 * OnHoldList) — one card per order, order-number search above the list,
 * PAGE_SIZE 25.
 *
 * The push-state split is WHO ACTS, spelled out in the badge text (never
 * colour alone — warehouse lighting): "Not yet pushed · our side" means we
 * still owe the identification tag + push in Shopify (own-org rows link
 * straight to the order in Shopify admin for exactly that); "Awaiting label ·
 * Portless" means their FC owes the label and the row clears itself. The
 * notify badge is deliberately muted — it is Slack-digest plumbing, not a
 * floor action, and must not compete with the one amber element per row.
 *
 * There is no resolve action, deliberately. These clear themselves the moment
 * the label-prefetch sweep (every 10 minutes) sees the label. An operator
 * "resolving" one by hand would only hide a parcel that still cannot ship,
 * which is the failure this whole feature exists to prevent.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { PackageX, Send, Clock, ExternalLink, FileUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ClientOrgChip } from '@/components/ClientOrgChip';
import { useClientOrgLabels } from '@/hooks/useClientOrgLabels';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useDateFnsLocale } from '@/lib/format-dates';
import { getShopifyAdminOrderUrl } from '@/lib/shopify-urls';
import { IssueFilterChips } from './IssueFilterChips';
import { IssueSearchInput } from './IssueSearchInput';
import { usePortlessBlocked } from '../hooks/usePortlessBlocked';
import { ManualLabelImportDialog } from '@/features/manual-labels';
import { trpc } from '@/lib/trpc';

const PAGE_SIZE = 25;

type PushFilter = 'all' | 'not_pushed' | 'awaiting_portless';

interface PortlessBlockedListProps {
  locationId: string;
}

export function PortlessBlockedList({ locationId }: PortlessBlockedListProps) {
  const { t } = useTranslation('production');
  const query = usePortlessBlocked(locationId);
  const { isCrossOrg, labelFor } = useClientOrgLabels();
  const { organization } = useOrganization();
  const { profile } = useAuth();
  const dateLocale = useDateFnsLocale();

  const [search, setSearch] = useState('');
  const [pushFilter, setPushFilter] = useState<PushFilter>('all');
  const [page, setPage] = useState(1);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [targetOrderId, setTargetOrderId] = useState<string | undefined>();
  const canImportManualLabels = [
    'Super Admin', 'Admin', 'Warehouse Coordinator', 'Warehouse Staff', 'Packer', 'CX',
  ].includes(profile?.role ?? '');
  const availability = trpc.manualLabels.getAvailability.useQuery(
    { locationId },
    { enabled: canImportManualLabels },
  );

  const rows = useMemo(() => query.data ?? [], [query.data]);

  const notPushedCount = useMemo(
    () => rows.filter((r) => r.pushState === 'not_pushed').length,
    [rows]
  );
  const awaitingCount = rows.length - notPushedCount;

  const isSearching = search.trim() !== '';
  const filtered = useMemo(() => {
    const needle = search.trim().replace(/^#/, '').toLowerCase();
    return rows.filter(
      (r) =>
        (pushFilter === 'all' || r.pushState === pushFilter) &&
        (needle === '' || (r.orderName ?? r.prodOrderId).toLowerCase().includes(needle))
    );
  }, [rows, pushFilter, search]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  if (query.isLoading) {
    return (
      <div className="space-y-3" aria-busy="true">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  // Silent when healthy: a floor with no blocked orders should see nothing at
  // all here, not an empty-state card competing for attention with real
  // issues. A failed REFETCH that retained data keeps rendering the stale
  // rows (react-query v5 keeps `data` on error) — a blank pane under a
  // sub-tab that says "292" would be worse than slightly stale cards.
  if (rows.length === 0 && !availability.data?.enabled) return null;

  const filterChips: Array<{ value: PushFilter; label: string; count: number }> = [
    { value: 'all', label: t('issues.portlessBlocked.filterAll'), count: rows.length },
    { value: 'not_pushed', label: t('issues.portlessBlocked.filterNotPushed'), count: notPushedCount },
    { value: 'awaiting_portless', label: t('issues.portlessBlocked.filterAwaiting'), count: awaitingCount },
  ];

  return (
    <div className="space-y-4">
      {/* The one thing an operator must know about each half of this list. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">{t('issues.portlessBlocked.description')}</p>
        {availability.data?.enabled && (
          <Button variant="outline" size="sm" className="gap-2" onClick={() => { setTargetOrderId(undefined); setUploadOpen(true); }}>
            <FileUp className="h-4 w-4" />{t('issues.portlessBlocked.importPdfs')}
          </Button>
        )}
      </div>

      {/* Search (+ live match count while narrowing) */}
      <div className="flex flex-wrap items-center gap-3">
        <IssueSearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder={t('issues.portlessBlocked.searchPlaceholder')}
          ariaLabel={t('issues.filters.searchAriaLabel')}
          clearLabel={t('issues.filters.clearSearch')}
        />
        {isSearching && (
          <span className="text-sm text-muted-foreground">
            {t('issues.searchMatchCount', { count: filtered.length })}
          </span>
        )}
      </div>

      {/* Who-acts filter pills — same anatomy as OnHoldList's reason pills
          (shared IssueFilterChips, amber accent = the original inline classes) */}
      <IssueFilterChips<PushFilter>
        options={filterChips}
        value={pushFilter}
        onChange={(value) => {
          setPushFilter(value);
          setPage(1);
        }}
        ariaLabel={t('issues.portlessBlocked.filterAria')}
      />

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <PackageX className="h-12 w-12 text-muted-foreground" aria-hidden="true" />
          <p className="mt-4 text-lg font-medium">{t('issues.portlessBlocked.noMatches')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginated.map((row) => {
            const notPushed = row.pushState === 'not_pushed';
            // Link own-org rows only: a cross-org row's order lives in the
            // CLIENT's Shopify admin, and the viewer's store slug would build
            // a wrong-store URL.
            const shopifyUrl = !isCrossOrg(row.organizationId)
              ? getShopifyAdminOrderUrl(organization?.shopify_domain, row.shopifyOrderId)
              : null;
            return (
              <Card key={row.prodOrderId}>
                <CardContent className="px-5 py-3.5">
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                    {/* Left: order + age + client */}
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      {shopifyUrl ? (
                        <a
                          href={shopifyUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                        >
                          {row.orderName ?? row.prodOrderId.slice(0, 8)}
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="text-sm font-semibold whitespace-nowrap">
                          {row.orderName ?? row.prodOrderId.slice(0, 8)}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" aria-hidden="true" />
                        {t('issues.portlessBlocked.waiting', {
                          time: formatDistanceToNow(new Date(row.blockedAt), { locale: dateLocale }),
                        })}
                      </span>
                      {isCrossOrg(row.organizationId) && (
                        <ClientOrgChip label={labelFor(row.organizationId)} />
                      )}
                    </div>

                    {/* Right: who acts (the only loud badge) + muted notify state */}
                    <div className="flex shrink-0 items-center gap-1.5">
                      {availability.data?.enabled && (
                        <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setTargetOrderId(row.prodOrderId); setUploadOpen(true); }}>
                          <FileUp className="h-3.5 w-3.5" />{t('issues.portlessBlocked.uploadPdf')}
                        </Button>
                      )}
                      {notPushed ? (
                        <Badge variant="warning" className="whitespace-nowrap">
                          {t('issues.portlessBlocked.notPushed')}
                        </Badge>
                      ) : (
                        <Badge variant="info" className="whitespace-nowrap">
                          {t('issues.portlessBlocked.awaitingPortless')}
                        </Badge>
                      )}
                      {row.notifyState === 'notified' ? (
                        <Badge variant="muted" className="gap-1 whitespace-nowrap">
                          <Send className="h-3 w-3" aria-hidden="true" />
                          {t('issues.portlessBlocked.notified')}
                        </Badge>
                      ) : (
                        <Badge variant="muted" className="gap-1 whitespace-nowrap">
                          <Clock className="h-3 w-3" aria-hidden="true" />
                          {t('issues.portlessBlocked.queued')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Pagination — same anatomy + keys as ShippingFailuresList */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm text-muted-foreground">
                {t('issues.pagination.showing', {
                  from: (safePage - 1) * PAGE_SIZE + 1,
                  to: Math.min(safePage * PAGE_SIZE, totalItems),
                  total: totalItems,
                })}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {t('issues.pagination.page', { current: safePage, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage(Math.max(1, safePage - 1))}
                >
                  {t('issues.pagination.previous')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(Math.min(totalPages, safePage + 1))}
                >
                  {t('issues.pagination.next')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      <ManualLabelImportDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        locationId={locationId}
        purpose="production_release"
        targetProdOrderId={targetOrderId}
        onApplied={() => void query.refetch()}
      />
    </div>
  );
}
